import {
  MeasuringTapeTractorBoundaryModelV1,
  TractorBoundaryModel,
} from "protos/atk/boundary_model";
import {
  optionalNumberToViewModel,
  OptionalViewModel,
  optionalViewModelToField,
} from "common/utils/viewModels";
import { Polygon2D } from "protos/geometry/geometry";
import { Tractor, TractorArchetype } from "protos/atk/autotractor";

/**
 * An even more view-model-y TractorBoundaryModel, with explicit "present" flags
 * for the optional fields so this model can be used with formik.
 */
export type TractorBoundaryViewModel = Omit<
  TractorBoundaryModel,
  "measuringTapeV1"
> & {
  measuringTapeV1:
    | (Omit<
        MeasuringTapeTractorBoundaryModelV1,
        "yNoseToFrontTireM" | "xCenterToWidestPtNoseM"
      > & {
        yNoseToFrontTireM: OptionalViewModel<number>;
        xCenterToWidestPtNoseM: OptionalViewModel<number>;
      })
    | undefined;
};

function tractorLikeOwnBoundaryViewModelOrDefault(
  tractorLike: TractorArchetype | Tractor | undefined
): OptionalViewModel<TractorBoundaryViewModel> {
  let present: boolean;
  let boundaryModel: TractorBoundaryModel;

  if (tractorLike?.boundaryModel) {
    // already a boundary model, nothing to do
    present = true;
    boundaryModel = tractorLike.boundaryModel;
  } else if (tractorLike?.boundary) {
    // TEMPORARY (to be removed once everything has a boundary model): probably
    // predated the existence of the boundary model, so infer one from the
    // boundary polygon

    // compute AABB of the boundary polygon
    let minX = 0;
    let maxX = 0;
    let minY = 0;
    let maxY = 0;

    for (const point of tractorLike.boundary.points) {
      if (point.x > maxX) {
        maxX = point.x;
      }
      if (point.x < minX) {
        minX = point.x;
      }
      if (point.y > maxY) {
        maxY = point.y;
      }
      if (point.y < minY) {
        minY = point.y;
      }
    }

    present = true;
    boundaryModel = {
      measuringTapeV1: {
        overallLengthM: maxY - minY,
        overallWidthM: maxX - minX,
        // nb: in a canonical polygon, (0, 0) is on the centerline of the front axle
        yNoseToFrontAxleM: maxY,
      },
    };
  } else {
    // neither a boundary model or boundary polygon, probably starting from
    // scratch here
    present = false;
    boundaryModel = {
      measuringTapeV1: {
        overallLengthM: 0,
        overallWidthM: 0,
        yNoseToFrontAxleM: 0,
      },
    };
  }

  return {
    present,
    value: {
      ...boundaryModel,
      measuringTapeV1: boundaryModel.measuringTapeV1
        ? {
            ...boundaryModel.measuringTapeV1,
            yNoseToFrontTireM: optionalNumberToViewModel(
              boundaryModel.measuringTapeV1.yNoseToFrontTireM
            ),
            xCenterToWidestPtNoseM: optionalNumberToViewModel(
              boundaryModel.measuringTapeV1.xCenterToWidestPtNoseM
            ),
          }
        : undefined,
    },
  };
}

export function tractorArchetypeBoundaryViewModel(
  archetype: TractorArchetype | undefined
): TractorBoundaryViewModel {
  return tractorLikeOwnBoundaryViewModelOrDefault(archetype).value;
}

export function tractorBoundaryViewModel(
  tractor: Tractor | undefined
): OptionalViewModel<TractorBoundaryViewModel> {
  const tractorBoundaryViewModel =
    tractorLikeOwnBoundaryViewModelOrDefault(tractor);
  const archetypeBoundaryViewModel = tractorLikeOwnBoundaryViewModelOrDefault(
    tractor?.archetype
  );

  return {
    present: tractorBoundaryViewModel.present,
    value: tractorBoundaryViewModel.present
      ? tractorBoundaryViewModel.value
      : archetypeBoundaryViewModel.value,
  };
}

export function persistTractorBoundaryViewModel(
  viewModel: TractorBoundaryViewModel,
  existingPolygon: Polygon2D | undefined
): { boundaryModel: TractorBoundaryModel; boundary: Polygon2D | undefined } {
  const boundaryModel: TractorBoundaryModel = {
    ...viewModel,
    measuringTapeV1: viewModel.measuringTapeV1
      ? {
          ...viewModel.measuringTapeV1,
          yNoseToFrontTireM: optionalViewModelToField(
            viewModel.measuringTapeV1.yNoseToFrontTireM
          ),
          xCenterToWidestPtNoseM: optionalViewModelToField(
            viewModel.measuringTapeV1.xCenterToWidestPtNoseM
          ),
        }
      : undefined,
  };

  if (boundaryModel.measuringTapeV1) {
    const halfWidth = boundaryModel.measuringTapeV1.overallWidthM / 2;
    const tailLength =
      boundaryModel.measuringTapeV1.overallLengthM -
      boundaryModel.measuringTapeV1.yNoseToFrontAxleM;

    return {
      boundaryModel,
      boundary: {
        points: [
          {
            x: -halfWidth,
            y: boundaryModel.measuringTapeV1.yNoseToFrontAxleM,
          },
          {
            x: halfWidth,
            y: boundaryModel.measuringTapeV1.yNoseToFrontAxleM,
          },
          {
            x: halfWidth,
            y: -tailLength,
          },
          {
            x: -halfWidth,
            y: -tailLength,
          },
        ],
      },
    };
  } else {
    // don't know how to bake this model down into a boundary; hand back the
    // polygon we had when the form was loaded
    return {
      boundaryModel,
      boundary: existingPolygon,
    };
  }
}
